import { useState, useRef, useEffect } from "react";
import { ACTIVE_BOOKING_URL } from "@/config/bookingLinks.js";

type Message = {
  id: string;
  role: "bot" | "user";
  text: string;
};

const quickReplies = [
  {
    label: "Help me find a therapist",
    response: ""
  },
  {
    label: "What are your fees?",
    response: ""
  },
  {
    label: "Do you offer in-person sessions?",
    response: ""
  },
  {
    label: "Do you take insurance?",
    response: ""
  },
  {
    label: "How do I book?",
    response: ""
  }
];

const welcomeText =
  "Hi, I'm here to help you explore therapist fit, services, fees, and next steps at Perspective Therapy. Please avoid sharing personal, clinical, or emergency information here. If this is an emergency, call 911 or 988.";

const genericResponse =
  "I can help with therapist fit, services, fees, and general next steps at Perspective Therapy. If you'd like help narrowing down therapist options, feel free to share what kind of support, communication style, or experience you're looking for.";

const markdownLinkPattern = /\[([^\]\n]+)\]\s*\((https?:\/\/[^\s)]+)\)/g;
const urlPattern = /https?:\/\/[^\s<>()]+[^\s<>().,!?;:\]}\/]/g;
const therapyPortalHostPattern = /(^|\.)therapyportal\.com$/i;
const fullUrlRequestPattern = /\b(full\s+(?:booking\s+)?url|visitor\s+(?:explicitly\s+)?asked\s+for\s+the\s+full\s+url)\b/i;

const linkClassName =
  "inline font-semibold text-[#3C6FF8] underline underline-offset-2 hover:text-[#2a5de6] focus:outline-none focus:ring-2 focus:ring-[#3C6FF8]/30 rounded-sm break-words max-w-full [overflow-wrap:anywhere] [word-break:break-word]";

function isHttpUrl(url: string) {
  return /^https?:\/\//i.test(url);
}

function normalizeUrl(url: string) {
  return url.replace(/\/+$/, "");
}

function isBookingUrl(url: string) {
  if (!isHttpUrl(url)) return false;

  try {
    const parsedUrl = new URL(url);
    const parsedBookingUrl = new URL(ACTIVE_BOOKING_URL);

    return (
      normalizeUrl(url).includes(normalizeUrl(ACTIVE_BOOKING_URL)) ||
      parsedUrl.hostname === parsedBookingUrl.hostname ||
      therapyPortalHostPattern.test(parsedUrl.hostname)
    );
  } catch {
    return false;
  }
}

function getRawUrlLabel(url: string, text: string) {
  if (!isBookingUrl(url) || fullUrlRequestPattern.test(text)) {
    return url;
  }

  return /booking portal/i.test(text) ? "booking portal" : "here";
}

function renderLink(url: string, label: string, key: string) {
  return (
    <a
      key={key}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClassName}
    >
      {label}
    </a>
  );
}

function renderRawUrls(text: string, keyPrefix: string) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  urlPattern.lastIndex = 0;

  while ((match = urlPattern.exec(text)) !== null) {
    const url = match[0];

    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(
      renderLink(url, getRawUrlLabel(url, text), `${keyPrefix}-url-${match.index}`)
    );

    lastIndex = match.index + url.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function renderMessageText(text: string) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  markdownLinkPattern.lastIndex = 0;

  while ((match = markdownLinkPattern.exec(text)) !== null) {
    const [fullMatch, label, url] = match;

    if (match.index > lastIndex) {
      parts.push(...renderRawUrls(text.slice(lastIndex, match.index), `text-${lastIndex}`));
    }

    parts.push(renderLink(url, label, `markdown-${match.index}`));

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(...renderRawUrls(text.slice(lastIndex), `text-${lastIndex}`));
  }

  return parts.length ? parts : text;
}

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: generateId(), role: "bot", text: welcomeText }
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

useEffect(() => {
  if (scrollRef.current) {
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }
}, [messages, typing]);

const addBotMessage = (text: string) => {
  setTyping(true);
  setTimeout(() => {
    setMessages((prev) => [...prev, { id: generateId(), role: "bot", text }]);
    setTyping(false);
  }, 600);
};

const handleSend = async (text: string) => {
  if (!text.trim()) return;

  const userMessage = text.trim();

  setMessages((prev) => [
    ...prev,
    { id: generateId(), role: "user", text: userMessage }
  ]);

  setInput("");
  setTyping(true);

  try {
    const response = await fetch("/api/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ message: userMessage })
    });

    const data = await response.json();

    setMessages((prev) => [
      ...prev,
      {
        id: generateId(),
        role: "bot",
        text:
          data.reply || genericResponse
      }
    ]);
  } catch (error) {
    setMessages((prev) => [
      ...prev,
      {
        id: generateId(),
        role: "bot",
        text:
          "I'm sorry, the concierge is temporarily unavailable right now."
      }
    ]);
  } finally {
    setTyping(false);
  }
};

const handleQuickReply = (qr: (typeof quickReplies)[0]) => {
  handleSend(qr.label);
};

useEffect(() => {
  const handler = () => setOpen(true);
  window.addEventListener("open-perspective-chat", handler);
  return () => window.removeEventListener("open-perspective-chat", handler);
}, []);


  const isContactPage = typeof window !== "undefined" && window.location.pathname === "/contact";
  const showLauncher = !isContactPage || open;
useEffect(() => {
  const handler = (e: Event) => {
    const label = (e as CustomEvent<{ label: string }>).detail?.label;
    if (!label) return;

    const matched = quickReplies.find((qr) => qr.label === label);
    if (matched) {
      setOpen(true);
      handleSend(matched.label);
    }
  };

  window.addEventListener("perspective-chat-quick-reply", handler);
  return () =>
    window.removeEventListener("perspective-chat-quick-reply", handler);
}, []);

  return (
    <>
      {/* Floating button */}
      {showLauncher && (
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] inline-flex items-center gap-2 text-white font-manrope font-semibold text-[13px] px-4 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap border"
        style={{
          background: open ? "#2B2B2B" : "#3C6FF8",
          borderColor: open ? "#2B2B2B" : "#3C6FF8",
        }}
        onMouseEnter={(e) => {
          if (!open) (e.currentTarget as HTMLButtonElement).style.background = "#2a5de6";
        }}
        onMouseLeave={(e) => {
          if (!open) (e.currentTarget as HTMLButtonElement).style.background = "#3C6FF8";
        }}
      >
        {open ? (
          <>
            <span>Close</span>
            <i className="ri-close-line text-sm"></i>
          </>
        ) : (
          <>
            <i className="ri-message-3-line text-sm"></i>
            <span>Need help finding the right therapist?</span>
          </>
        )}
      </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-[59] w-[340px] max-w-[calc(100vw-3rem)] rounded-2xl border overflow-hidden flex flex-col"
          style={{
            background: "rgba(255,255,255,0.97)",
            borderColor: "rgba(203,191,175,0.40)",
            height: "460px",
            maxHeight: "calc(100vh - 8rem)",
          }}
        >
          {/* Header */}
          <div
            className="shrink-0 px-5 py-4 border-b flex items-center justify-between"
            style={{
              background: "linear-gradient(135deg, #3C6FF8 0%, #7D54FC 100%)",
              borderColor: "rgba(255,255,255,0.12)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-chat-smile-3-line text-white text-sm"></i>
              </div>
              <div>
                <h4 className="font-fraunces text-white text-[15px] font-medium leading-tight">
                  Therapy Concierge
                </h4>
                <p className="font-manrope text-white/75 text-[11px] leading-tight">
                  Here to help you find the right next step
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-sm"></i>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="max-w-[85%] min-w-0 rounded-xl px-3.5 py-2.5 font-manrope text-[13px] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word]"
                  style={
                    msg.role === "user"
                      ? {
                          background: "#3C6FF8",
                          color: "#fff",
                          borderRadius: "12px 12px 4px 12px",
                        }
                      : {
                          background: "#F5F1E8",
                          color: "#2B2B2B",
                          borderRadius: "12px 12px 12px 4px",
                        }
                  }
                >
                  {msg.role === "bot" ? renderMessageText(msg.text) : msg.text}
                </div>
              </div>
            ))}

            {typing && (
              <div className="flex justify-start">
                <div
                  className="rounded-xl px-3.5 py-2.5 flex items-center gap-1"
                  style={{
                    background: "#F5F1E8",
                    borderRadius: "12px 12px 12px 4px",
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#2B2B2B]/40 animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#2B2B2B]/40 animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#2B2B2B]/40 animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Quick replies */}
          <div className="shrink-0 px-4 pb-2 flex flex-wrap gap-1.5">
            {quickReplies.map((qr) => (
              <button
                key={qr.label}
                onClick={() => handleQuickReply(qr)}
                className="font-manrope text-[11px] font-semibold px-2.5 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                style={{
                  color: "#3C6FF8",
                  borderColor: "rgba(60,111,248,0.25)",
                  background: "rgba(60,111,248,0.06)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#3C6FF8";
                  (e.currentTarget as HTMLButtonElement).style.color = "#fff";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#3C6FF8";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(60,111,248,0.06)";
                  (e.currentTarget as HTMLButtonElement).style.color = "#3C6FF8";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(60,111,248,0.25)";
                }}
              >
                {qr.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div
            className="shrink-0 px-4 py-3 border-t flex items-center gap-2"
            style={{ borderColor: "rgba(203,191,175,0.35)" }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSend(input);
              }}
              placeholder="Type a message..."
              className="flex-1 font-manrope text-[13px] px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-[#3C6FF8]/20 transition-all bg-white"
              style={{ borderColor: "rgba(203,191,175,0.45)" }}
            />
            <button
              onClick={() => handleSend(input)}
              disabled={!input.trim() || typing}
              className="w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer disabled:opacity-40"
              style={{ background: input.trim() && !typing ? "#3C6FF8" : "rgba(203,191,175,0.35)" }}
            >
              <i className="ri-send-plane-fill text-white text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
